import { Suspense } from 'react';
import { Navigate } from 'react-router-dom';
import { Outlet } from 'react-router-dom';
import { RouterProvider } from 'react-router-dom';
import { createBrowserRouter } from 'react-router-dom';
import GlobalStyles from '@mui/material/GlobalStyles';
import { ToastProvider } from '@socialgoodsoftware/components';
import { FeatureProvider } from '@socialgoodsoftware/components';
import { RequireFeature } from '@socialgoodsoftware/components';
import { AppShell } from './shell/AppShell';
import { ThemeSync } from './shell/ThemeSync';
import { RequireAuth } from './components/auth/RequireAuth';
import { featureRoutes } from './config/feature-routes';
import { useDocumentTitle } from './lib/document-title';
import { OrganizationProvider } from './contexts/OrganizationContext';
import { useOrganization } from './contexts/OrganizationContext';
import { RunAsProvider } from './contexts/RunAsContext';

import type { FeatureRoute } from './config/feature-routes';
import type { ReactElement } from 'react';

const guestOnlyPaths = new Set(['/login', '/signup']);

// This app signs a member in without reloading the page, so the provider mounts as a guest and
// has to be told who arrived. Reading the user here rather than in RootLayout is what puts it
// inside OrganizationProvider.
function AccountFeatures({ children }: { children: ReactElement }): ReactElement {
  const { user } = useOrganization();
  return <FeatureProvider sessionKey={user?.email}>{children}</FeatureProvider>;
}

// Root layout — mounts every provider that depends on React Router hooks
// (Organization uses useNavigate/useLocation/useSearchParams; the others sit
// here to keep the provider tree co-located).
function RootLayout(): ReactElement {
  useDocumentTitle();
  return (
    <RunAsProvider>
      <ToastProvider>
        <OrganizationProvider>
          <AccountFeatures>
            <>
              <ThemeSync />
              <Outlet />
            </>
          </AccountFeatures>
        </OrganizationProvider>
      </ToastProvider>
    </RunAsProvider>
  );
}

function renderRouteElement(route: FeatureRoute): ReactElement {
  if (route.redirectTo !== undefined) {
    return <Navigate to={route.redirectTo} replace />;
  }
  const RouteComponent = route.component as NonNullable<FeatureRoute['component']>;
  const page = (
    <Suspense fallback={route.skeleton}>
      <RouteComponent />
    </Suspense>
  );
  // Auth pages provide their own full-viewport layout via AuthLayout, so they
  // mount outside the AppShell (no sidebar/header on sign-in screens).
  if (route.archetype === 'auth') {
    let authElement: ReactElement;
    if (guestOnlyPaths.has(route.path)) {
      authElement = <RequireAuth mode="guest">{page}</RequireAuth>;
    } else {
      authElement = page;
    }
    return authElement;
  }
  return (
    <RequireAuth>
      <AppShell>
        <RequireFeature groupGuid={route.groupGuid} featureGuid={route.featureGuid}>
          {page}
        </RequireFeature>
      </AppShell>
    </RequireAuth>
  );
}

const router = createBrowserRouter([
  {
    element: <RootLayout />,
    children: [
      ...featureRoutes.map((route) => ({
        path: route.path,
        element: renderRouteElement(route),
      })),
      {
        path: '*',
        element: <Navigate to="/dashboard" replace />,
      },
    ],
  },
]);

const globalResetStyles = {
  'html, body, #root': {
    margin: 0,
  },
  body: {
    backgroundColor: 'var(--color-bg)',
  },
};

export function App() {
  return (
    <>
      <GlobalStyles styles={globalResetStyles} />
      <RouterProvider router={router} />
    </>
  );
}
